import React, { useCallback, useEffect, useRef, useState } from "react";
import { NotificationManager } from "react-notifications";

import "./TemplateEditOverlay.scss";
import { useAppSelector } from "../../../../store/hooks";

import { AllComponents } from "../../../_default/interfaces/base";
import { updateEditor } from "../../../../store/reducers/builderReducers/editor/editorActions";
import { setStructure } from "../../../../store/reducers/builderReducers/structure/structureActions";
import StructureAPI from "../../../../store/reducers/builderReducers/structure/structureApi";
import { setEditableComponentId } from "../../../../store/reducers/textEditor/textEditorActions";
import {
  getComponentFromStructureMap,
  withStableComponentId,
} from "../../../_default/utilities/componentIdentity";
import type { Editor } from "../../../../store/reducers/builderReducers/editor/editorTypes";
import { Close } from "@mui/icons-material";
import type { StructureMap } from "../../../../store/reducers/builderReducers/structure/structureReducer";
import {
  findTemplateForComponent,
  getTemplateSourceComponent,
} from "../../../../store/reducers/builderReducers/templates/templateHelpers";
import TemplateAPI, { Template } from "../../../../store/reducers/builderReducers/templates/templateApi";
import {
  cloneComponentForSave,
  buildComponentsMapForSave,
  isEditorSentinelComponent,
  isEditorSentinelRelation,
  toTemplateChildren,
} from "../../../_default/utilities/templateStructureForSave";

declare var defaultComponents: any;

function normalizeTemplateTree(
  source: AllComponents,
  parent?: Pick<AllComponents, "id" | "uuid">,
  fallbackComponents?: StructureMap["components"]
): AllComponents {
  const byUuid = new Map<string, AllComponents>();

  const indexNode = (node: any, visited: Set<any>) => {
    if (!node || typeof node !== "object" || visited.has(node)) {
      return;
    }
    if (isEditorSentinelComponent(node)) {
      return;
    }
    visited.add(node);
    const nodeUuid = typeof node.uuid === "string" ? node.uuid.trim() : "";
    if (nodeUuid) {
      byUuid.set(nodeUuid, node);
    }
    for (const child of toTemplateChildren(node)) {
      indexNode(child, visited);
    }
    if (node.components && typeof node.components === "object") {
      Object.values(node.components).forEach((child) => indexNode(child, visited));
    }
  };

  indexNode(source, new Set<any>());
  Object.values(fallbackComponents ?? {}).forEach((node) => {
    if (!node || isEditorSentinelComponent(node)) {
      return;
    }
    const nodeUuid = node?.uuid?.trim();
    if (nodeUuid) {
      byUuid.set(nodeUuid, node);
    }
  });

  const visitedUuids = new Set<string>();
  const hydrate = (
    node: AllComponents,
    parentNode?: Pick<AllComponents, "id" | "uuid">
  ): AllComponents => {
    const nodeUuid = node.uuid?.trim();
    if (nodeUuid && visitedUuids.has(nodeUuid)) {
      return { ...node, childElements: [] } as AllComponents;
    }
    if (nodeUuid) {
      visitedUuids.add(nodeUuid);
    }
    const directChildren = toTemplateChildren(node)
      .map((child) => {
        const childUuid = child?.uuid?.trim();
        if (childUuid) {
          return byUuid.get(childUuid) ?? child;
        }
        return child;
      })
      .filter((child) => !isEditorSentinelComponent(child));
    const relations = Array.isArray(node.childWebpageComponentRelations)
      ? node.childWebpageComponentRelations
      : [];
    const filteredRelations = relations.filter(
      (relation) => !isEditorSentinelRelation(relation)
    );
    const relationChildren =
      directChildren.length > 0
        ? []
        : filteredRelations
            .map((relation) => {
              const childUuid =
                typeof relation?.child_uuid === "string"
                  ? relation.child_uuid.trim()
                  : "";
              if (childUuid) {
                return byUuid.get(childUuid) ?? null;
              }
              return null;
            })
            .filter((x): x is AllComponents => !!x);
    const children = (directChildren.length > 0
      ? directChildren
      : relationChildren
    ).map((child) => hydrate(child as AllComponents, node));

    const nextRelations =
      filteredRelations.length > 0
        ? filteredRelations
        : children.map((child, index) => ({
            id: 0,
            parent_id: node.id ?? 0,
            parent_uuid: node.uuid?.trim() ?? null,
            child_id: child.id ?? 0,
            child_uuid: child.uuid?.trim() ?? null,
            sort: index + 1,
            created_at: null,
            updated_at: null,
          }));

    return {
      ...node,
      parent_id:
        node.parent_id != null
          ? node.parent_id
          : parentNode?.id != null
            ? parentNode.id
            : null,
      ...(node.parent_uuid?.trim()
        ? { parent_uuid: node.parent_uuid }
        : parentNode?.uuid?.trim()
          ? { parent_uuid: parentNode.uuid }
          : {}),
      childElements: children,
      childWebpageComponentRelations: nextRelations as any,
    } as AllComponents;
  };

  return hydrate(source, parent);
}

function buildOverlayStructure(root: AllComponents): StructureMap {
  const components: StructureMap["components"] = {};
  const stack: AllComponents[] = [root];
  const rootKey = root.uuid?.trim() ?? "";
  while (stack.length > 0) {
    const current = stack.pop()!;
    const key = current.uuid?.trim() ?? "";
    if (!key) {
      continue;
    }
    components[key] = current;
    for (const child of toTemplateChildren(current)) {
      stack.push(child as AllComponents);
    }
  }
  return {
    webpageComponents: rootKey ? [rootKey] : [],
    components,
  };
}

function cloneStructureSnapshot(value: StructureMap): StructureMap {
  return JSON.parse(JSON.stringify(value));
}

export default function TemplateEditOverlay() {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const editor = useAppSelector((state) => state.builder.editor);
  const templates = useAppSelector((state) => state.builder.templates);
  const activePageUuid = useAppSelector((state) => state.builder.pages.active);

  const structure = useAppSelector((state) => state.builder.structure);
  const [activeTemplate, setActiveTemplate] = useState<Template | null>(null);
  const [component, setComponent] = useState<AllComponents | null>(null);
  const overlayInitialized = useRef(false);
  const originalStructureRef = useRef<StructureMap | null>(null);
  const originalHasEditedRef = useRef<boolean>(false);
  const skipRestoreOnCloseRef = useRef(false);

  // Add event listener to ESC key, if pressed, change the open state to false

  const handleCloseEditor = useCallback(() => {
    if (originalStructureRef.current && !skipRestoreOnCloseRef.current) {
      setStructure(cloneStructureSnapshot(originalStructureRef.current));
    }
    setOpen(false);
    setComponent(null);
    setActiveTemplate(null);
    setEditableComponentId(null);
    overlayInitialized.current = false;
    originalStructureRef.current = null;
    skipRestoreOnCloseRef.current = false;
    updateEditor({
      ...(editor as Editor),
      hasEdited: originalHasEditedRef.current,
      template: null,
      component_id: null,
      component_parent_id: null,
      component_uuid: null,
      component_parent_uuid: null,
    });

    const sidebar = document.querySelector(".sidebar ul");
    if (sidebar) {
      const activeElement = sidebar.querySelector(".active") as HTMLLIElement;
      if (activeElement) {
        activeElement.click();
      }
    }
  }, [editor]);

  const handleSaveTemplate = useCallback(() => {
    if (!activeTemplate || !component) {
      return;
    }
    const rootKey = structure.webpageComponents[0];
    const rootComponent = rootKey ? structure.components[rootKey] : null;
    if (!rootComponent) {
      return;
    }

    setIsSaving(true);
    const savedTree = cloneComponentForSave(
      rootComponent as AllComponents,
      structure.components ?? {},
      new Set<string>()
    );
    const savedComponents = buildComponentsMapForSave(savedTree);
    TemplateAPI.saveTemplateById({
      ...activeTemplate,
      component: savedTree,
      components: savedComponents,
      rootComponentUuid: savedTree.uuid?.trim() || activeTemplate.rootComponentUuid,
    } as Template)
      .then(() => {
        NotificationManager.success("Template saved successfully.", null, 3000);
        // Save path should keep server-updated shared UUID tree, not restore stale pre-overlay snapshot.
        skipRestoreOnCloseRef.current = true;
        handleCloseEditor();
        if (activePageUuid) {
          StructureAPI.loadWebpageComponents(activePageUuid).catch((error) => {
            console.error("Template save: failed to reload page structure", error);
          });
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [activeTemplate, component, structure, handleCloseEditor, activePageUuid]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseEditor();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    handleCloseEditor,
  ]);

  useEffect(() => {
    if (editor.template) {
      const resolvedTemplate =
        findTemplateForComponent(templates, editor.template) ?? null;
      const source = getTemplateSourceComponent(
        resolvedTemplate ??
          ({
            component: editor.template,
            webpage_component: editor.template,
          } as any)
      );
      if (!source) {
        return;
      }
      const fallbackComponents =
        originalStructureRef.current?.components ?? structure.components ?? {};
      const normalizedRoot = normalizeTemplateTree(
        source,
        undefined,
        fallbackComponents
      );
      const isFirstOverlayInit = !overlayInitialized.current;
      if (isFirstOverlayInit) {
        originalStructureRef.current = cloneStructureSnapshot(structure);
        originalHasEditedRef.current = !!editor.hasEdited;
        overlayInitialized.current = true;
      }
      const overlayStructure = buildOverlayStructure(normalizedRoot);
      const keys = Object.keys(overlayStructure.components ?? {});
      setStructure(overlayStructure);

      setOpen(true);
      setActiveTemplate(resolvedTemplate);
      setComponent(normalizedRoot.uuid?.trim() ? normalizedRoot : null);
      setEditableComponentId(null);
      updateEditor({
        ...editor,
        component_uuid: null,
        component_id: null,
        component_parent_uuid: null,
        component_parent_id: null,
      });
      return;
    }
    if (overlayInitialized.current) {
      handleCloseEditor();
      return;
    }
    setOpen(false);
    setComponent(null);
    setActiveTemplate(null);
  }, [editor.template, templates]);

  let ComponentToRender;
  const liveRootComponent = component?.uuid?.trim()
    ? getComponentFromStructureMap(structure.components, component.uuid.trim()) ?? null
    : null;

  const templateType = liveRootComponent?.type;

  if (liveRootComponent && templateType) {
    ComponentToRender = defaultComponents[templateType];
  }

  const renderRootComponent = liveRootComponent
    ? withStableComponentId(liveRootComponent)
    : null;

  return (
    <div
      id="template_edit_over_lay"
      className="template-edit-overlay"
      style={{
        display: open ? "flex" : "none",
        width: `calc(100% - 440px)`,
        zIndex: 9999,
      }}
    >
      <div
        className="template-edit-overlay__content"
        id="template_edit_over_lay_content"
      >
        {editor.template &&
          renderRootComponent &&
          ComponentToRender && (
          <ComponentToRender
            key={renderRootComponent.uuid?.trim() ?? ""}
            {...renderRootComponent}
          />
        )}
      </div>

      <div className="template-edit-overlay__actions">
        <button
          className="template-edit-overlay__save"
          onClick={handleSaveTemplate}
          disabled={isSaving}
          type="button"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
        <div className="template-edit-overlay__close" onClick={handleCloseEditor}>
          Exit
          <Close style={{ cursor: "pointer", fontSize: "22px" }} />
        </div>
      </div>
    </div>
  );
}
